import {
  Flexbox,
  Link,
  SelectionCheckbox,
  Toggle,
} from "@sede-enterprise/shell-ds-react-framework";
import dayjs, { type Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { twohundred } from "../../constants";
import type { EventValue, IDeals, IFilters, IRow, Idispatch } from "../../types/types";
import { notify } from "../Toast/notify";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const tableGap = "20px";
export const EmptyHeader = () => <></>;
export const EmptyHeaderCell = (
  { row, getValue }: { row: IRow; getValue: () => string },
  selectedData: IDeals[],

  handleSingleCheckbox: (selectedItem: IDeals) => void
) => {
  return (
    <Flexbox gap={tableGap} alignItems="center">
      <SelectionCheckbox
        data-testid="checkbox"
        checked={selectedData.some(
          (item: IDeals) => item.OPPORTUNITY_ID === row.original.OPPORTUNITY_ID
        )}
        indeterminate={row.getIsSomeSelected()}
        onChange={(event) => {
          row.getToggleSelectedHandler()(event);
          handleSingleCheckbox(row.original);
        }}
      />
      {getValue()}
    </Flexbox>
  );
};

export const dealMovedToDashboard = (
  { row, getValue }: { row: IRow; getValue: () => string },

  handleToggle: (selectedItem: IDeals) => Promise<void>
) => {
  const toggleLabel = Number(getValue()) === 0 ? "No" : "Yes";
  return row.original.MOVE_CARBON_DASHBOARD !== null ? (
    <Flexbox gap={tableGap} alignItems="center">
      {toggleLabel as string}
      <Toggle
        size="medium"
        data-testid="toggle"
        checked={row.original.MOVE_CARBON_DASHBOARD === 1}
        onChange={() => handleToggle(row.original)}
      />
    </Flexbox>
  ) : (
    "-"
  );
};
export const opportunityNameModal = (
  { row, getValue }: { row: IRow; getValue: () => string },

  handleModal: (selectedOpportunityId: number, isOperatingPlan: number) => void
) => {
  return (
    <Flexbox gap={tableGap} alignItems="center">
      <Link
        data-testid="modal"
        onClick={() => handleModal(row.original.OPPORTUNITY_ID, row.original.IS_OPERATING_PLAN)}
        className="modal-link"
      >
        {getValue()}
      </Link>
    </Flexbox>
  );
};
export const showToast = (status: number, message: string) => {
  if (status === twohundred) {
    notify("success", message);
  } else {
    notify("error", message);
  }
};

export const handleDateChange = (
  e: EventValue<Dayjs> | { $d: Date },
  dispatch: Idispatch,
  type: string
) => {
  if (e && "$d" in e && e.$d !== undefined) {
    dispatch({
      type,
      payload: dayjs(e.$d).format("YYYY-MM-DD"),
    });
  } else {
    dispatch({
      type,
      payload: null,
    });
  }
};

export const applyFilter = (allDealsData: IDeals[], filters: IFilters) => {
  const { opportunity, commodity, country, startDate, endDate, isEquityScope } = filters;
  const filterFunctions = [
    (deal: IDeals) =>
      opportunity.length !== 0 ? opportunity.includes(deal.OPPORTUNITY_NAME) : true,
    (deal: IDeals) => (commodity ? deal.TRADE_COMMODITY_NAME === commodity : true),
    (deal: IDeals) => (country ? deal.COUNTRY_NAME === country : true),
    (deal: IDeals) =>
      startDate ? dayjs(deal.START_DATE).isSameOrAfter(dayjs(String(startDate))) : true,
    (deal: IDeals) =>
      endDate ? dayjs(deal.END_DATE).isSameOrBefore(dayjs(String(endDate))) : true,
    (deal: IDeals) => (isEquityScope ? deal.EQUITY_SCOPE !== null : true),
  ];

  return allDealsData.filter((deal) => filterFunctions.every((fn) => fn(deal)));
};

export const disabledPrevDatesForEndDate = (current: Dayjs, filters: IFilters) =>
  current != null ? current.isBefore(filters.startDate, "date") : false;

export const disabledNextDatesForStartDate = (current: Dayjs, filters: IFilters) =>
  current != null ? current.isAfter(filters.endDate, "date") : false;

export const isAnyFieldNotEmpty = (filters: IFilters) => {
  const { opportunity, commodity, country, startDate, endDate, isEquityScope } = filters;
  return [
    opportunity.length === 0,
    commodity === null,
    country === null,
    !startDate,
    !endDate,
    !isEquityScope,
  ].every(Boolean);
};

export const roundToDecimals = (value: number, decimals: number) => {
  return Number.parseFloat(value.toFixed(decimals));
};
